<template>
  <PSelect
    ref="select"
    class="bz-select"
    overlay-class="bz-select-overlay"
    option-label="label"
    option-value="value"
    append-to="body"
    :options="options"
    :model-value="modelValue"
    :aria-labelledby="labelledBy"
    :pt="pt"
    @update:model-value="(value) => $emit('update:modelValue', value)"
    @before-show="onShow"
    @before-hide="onHide"
  >
    <!-- Every option's short name, stacked in one grid cell and hidden but the chosen one: the value is
         as wide as its widest option, so the row never moves under a finger. `data-bz-slot` marks what
         is the app's markup inside the library's, for the contract test. -->
    <template #value="{ value }">
      <span class="bz-select__sizer" data-bz-slot>
        <span
          v-for="option in options"
          :key="String(option.value)"
          :class="{ 'is-chosen': option.value === value }"
          :aria-hidden="option.value === value ? undefined : 'true'"
        >{{ option.short || option.label }}</span>
      </span>
    </template>
    <template #dropdownicon>
      <BzIcon name="unfold_more" class="bz-select__chevron" data-bz-slot />
    </template>
    <!-- An option: its words, and a check on the chosen one. -->
    <template #option="{ option, selected }">
      <span class="bz-select__option" data-bz-slot>
        <span class="bz-select__option-label">{{ option.label }}</span>
        <BzIcon v-if="selected" name="check" class="bz-select__option-check" />
      </span>
    </template>
  </PSelect>
</template>

<script>
import Select from 'primevue/select';
import { openLayer } from '@/ui/backClose';
import BzIcon from './BzIcon.vue';

/**
 * A choice in a list row, as both systems' settings draw one: the row's name on the left, the chosen
 * value on the right in the second ink with an unfold icon, and the whole row the target; a tap opens
 * the list as a menu that floats, the chosen option in the selection's blue and checked. Not a field:
 * an input box inside a list reads as a web form (feedback 2-oct-2026). Its colours and sizes are the
 * preset's (src/ui/preset.js).
 *
 * @rule   `options` are `{ value, label, short? }`: the row shows `short` (or the label), the menu the
 *         full label. `labelledBy` is the id of the visible label, the select's accessible name.
 * @rule   It lives in a `BzListRow`: there the row is its target (a layer stretched over the row) and
 *         the row lights while the finger holds it and while its list is open.
 * @rule   With the list open, Android's back closes the list and nothing else (src/ui/backClose.js).
 * @why    The native select opened Android's own picker, which back dismissed. The library's list
 *         is drawn by the page, and without this back would leave the app.
 * @why    On `before-show`/`before-hide`, not `show`/`hide`: `show` comes when the list's entrance
 *         transition ends, and a back pressed during those ~120 ms — the list already on screen —
 *         would still leave the app (the e2e test caught it).
 * @rule   With a keyboard its row wears the system's focus ring, drawn inside the row as on a row that is a
 *         button: the target is the whole row. PrimeVue's own ring is off in the preset, since it would
 *         light up on a tap too.
 * @rule   The value stays on the row's line on a phone too, as iOS writes it (Adrián, 14-oct-2026): the
 *         screen gives it a `short` that fits there (SettingsScreen's resolution drops its pixels).
 * @rule   48 px tall at least (norm 9), and as wide as its widest short name.
 * @why    PrimeVue's field takes its width from outside (its label is `width: 1%`); left alone, the
 *         three selects of Settings would change width with each choice, and the row under a finger
 *         would move.
 */
export default {
  name: 'BzSelect',
  components: { PSelect: Select, BzIcon },
  props: {
    modelValue: { type: [String, Number, Boolean], default: null },
    options: { type: Array, required: true },
    labelledBy: { type: String, required: true },
    // The id of the row's hint: the choice is described by it, as a switch is.
    describedBy: { type: String, default: undefined },
  },
  emits: ['update:modelValue'],
  data() {
    return { closeLayer: null };
  },
  computed: {
    pt() {
      return {
        transition: { onEnter: this.alignToRow },
        label: this.describedBy ? { 'aria-describedby': this.describedBy } : {},
      };
    },
  },
  beforeUnmount() {
    this.onHide();
  },
  methods: {
    onShow() {
      this.closeLayer = openLayer(() => {
        this.closeLayer = null;
        if (this.$refs.select) this.$refs.select.hide();
      });
    },
    onHide() {
      const closed = this.closeLayer;
      this.closeLayer = null;
      if (closed) closed();
    },
    /**
     * The list lined up with the right edge of the row that opened it, on every width. PrimeVue lines its
     * left edge up with the value's, and the value of a row is narrow: the list stuck out of the card on a
     * wide tablet, and flipped to the right on a narrow one. Runs in the list's entrance, after PrimeVue
     * has placed it and before the first frame, so nothing jumps.
     */
    alignToRow(el) {
      const row = this.$el && this.$el.closest('.bz-list-row');
      if (!row) return;
      const box = row.getBoundingClientRect();
      const left = Math.max(box.left, box.right - el.offsetWidth) + window.scrollX;
      el.style.insetInlineStart = `${left}px`;
    },
  },
};
</script>

<style scoped>
.bz-select {
  max-width: 100%;
  min-height: var(--bz-target-min);
  align-items: center;
}


.bz-select :deep(.p-select-label) {
  width: auto;
  min-width: 0;
  padding-inline-end: 0;
  font-family: var(--bz-font);
  letter-spacing: var(--bz-tracking-md);
  text-align: right;
}

.bz-select__sizer {
  display: inline-grid;
  max-width: 100%;
}

.bz-select__sizer > span {
  grid-area: 1 / 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  visibility: hidden;
}

.bz-select__sizer > span.is-chosen {
  visibility: visible;
}

.bz-select :deep(.p-select-dropdown) {
  justify-content: flex-end;
}

.bz-select__chevron {
  font-size: var(--bz-icon-sm);
}
</style>

<style>
/* Unscoped, because they reach across components (the row is BzListRow's markup, the select is inside
   it) — and not with `:global(.row) > .bz-select` in the scoped block: Vue keeps only the `:global()`
   part of such a selector and drops the rest, which put the rule on the row's control box instead. */

/* In a row, the row is the target: a layer over the whole row, which the row positions. It hangs off the
   select's focusable label, not off its root: a tap on the root has PrimeVue move the focus by script,
   and Chrome then lights the keyboard's ring; a tap on the label focuses it as a tap does. */
.bz-list-row__control > .bz-select,
.bz-list-row__control > .bz-select .p-select-label {
  position: static;
}

.bz-list-row__control > .bz-select .p-select-label::after {
  content: '';
  position: absolute;
  inset: 0;
}

/* In a row the select keeps its 48 but lends the line only the name's 24: it hangs 12 above and below,
   over the row's own padding, so the hint sits under the name as in a row with a switch. The tap is the
   row's anyway (the layer above). */
.bz-list-row__control > .bz-select {
  margin-block: calc((var(--bz-leading-lg) - var(--bz-target-min)) / 2);
}

/* The row of a select lights while a finger holds it (at once, and lets go in 150 ms, as BzListRow's
   pressable rows) and while its list is open, which says whose list it is. */
.bz-list-row:has(> .bz-list-row__control > .bz-select:active),
.bz-list-row:has(> .bz-list-row__control > .p-select-open) {
  background: var(--bz-control);
}

.bz-list-row:has(> .bz-list-row__control > .bz-select:active) {
  transition-duration: 0s;
}

/* With a keyboard, the ring on the row, inside it, as a row that is a button wears it. */
.bz-list-row:has(> .bz-list-row__control > .bz-select .p-select-label:focus-visible) {
  outline: 3px solid var(--bz-focus);
  outline-offset: -3px;
}

/* The list is teleported to <body>, out of reach of the scoped styles above. */
.bz-select-overlay {
  color-scheme: dark;
  font-family: var(--bz-font);
  font-optical-sizing: auto;
  font-synthesis: none;
  font-kerning: normal;
}

.bz-select-overlay .p-select-option {
  min-height: var(--bz-target-md);
}

.bz-select__option {
  display: flex;
  align-items: center;
  gap: var(--bz-space-3);
  width: 100%;
}

.bz-select__option-label {
  flex: 1;
  min-width: 0;
  letter-spacing: var(--bz-tracking-md);
}

.bz-select__option-check {
  font-size: var(--bz-icon-sm);
  color: var(--bz-action-ink);
}
</style>
